/**
 * Formulário único de edição do paciente (Meu Perfil).
 * Fonte de verdade alinhada ao prontuário (mesmos campos da tabela patients).
 */

import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Heart, Save, Loader2, Scale, AlertTriangle, User, MapPin } from 'lucide-react';
import { updatePatient, type PatientDTO } from '@/services/patients';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  patientDtoToFormValues,
  validateAndBuildPatientProfilePayload,
  type PatientProfileFormValues,
} from '@/lib/patientEditableProfile';
import { BLOOD_TYPE_SELECT_OPTIONS } from '@/lib/bloodType';
import { ConditionalTextarea } from '@/components/ui/conditional-textarea';
import { AccountCredentialsSection } from '@/components/auth/AccountCredentialsSection';
import { formatPhone, formatCpf, formatCep } from '@/utils/masks';
import type { UserDTO } from '@/types/auth';

interface PatientProfileEditorProps {
  patient: PatientDTO;
  accountName?: string;
  accountEmail?: string;
  onUpdate: (updatedPatient: PatientDTO) => void;
  onAccountEmailUpdated?: (user: UserDTO) => void;
}

export function PatientProfileEditor({
  patient,
  accountName,
  accountEmail,
  onUpdate,
  onAccountEmailUpdated,
}: PatientProfileEditorProps) {
  const [values, setValues] = useState<PatientProfileFormValues>(() =>
    patientDtoToFormValues(patient),
  );
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof PatientProfileFormValues, string>>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [imc, setImc] = useState<number | null>(null);

  useEffect(() => {
    setValues(patientDtoToFormValues(patient));
  }, [patient]);

  useEffect(() => {
    const h = parseFloat(values.height);
    const w = parseFloat(values.weight);
    if (h > 0 && w > 0) {
      setImc(parseFloat((w / (h * h)).toFixed(2)));
    } else {
      setImc(null);
    }
  }, [values.height, values.weight]);

  const updateField = <K extends keyof PatientProfileFormValues>(
    field: K,
    value: PatientProfileFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const getImcInfo = (value: number) => {
    if (value < 18.5) {
      return {
        label: 'Abaixo do peso',
        color:
          'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/30',
      };
    }
    if (value < 25) {
      return {
        label: 'Peso normal',
        color:
          'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/30',
      };
    }
    if (value < 30) {
      return {
        label: 'Sobrepeso',
        color:
          'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-900/30',
      };
    }
    return {
      label: 'Obesidade',
      color:
        'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/10 border-rose-200 dark:border-rose-900/30',
    };
  };

  const handleSave = async () => {
    const result = validateAndBuildPatientProfilePayload(values);
    if (!result.valid || !result.payload) {
      setFieldErrors(result.errors);
      const firstError = Object.values(result.errors)[0];
      toast.error(firstError || 'Verifique os campos do formulário.');
      return;
    }

    try {
      setIsSaving(true);
      const response = await updatePatient(patient.id, result.payload);
      if (response.success && response.data) {
        toast.success('Perfil atualizado com sucesso!');
        onUpdate(response.data);
      } else {
        toast.error(response.error || 'Erro ao salvar perfil.');
      }
    } catch (error) {
      console.error('Error saving patient profile:', error);
      toast.error('Erro ao conectar com o servidor.');
    } finally {
      setIsSaving(false);
    }
  };

  const imcDetails = imc ? getImcInfo(imc) : null;
  const todayISO = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6">
      <Card className="border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden">
        <CardHeader className="border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/30">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-teal-600" />
            <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
              Conta
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Nome</Label>
            <Input
              value={accountName || patient.user?.name || '—'}
              disabled
              readOnly
              className="bg-gray-50 dark:bg-neutral-800/80 border-gray-200 dark:border-neutral-700"
            />
          </div>
        </CardContent>
      </Card>

      <AccountCredentialsSection
        email={accountEmail || patient.user?.email || ''}
        onEmailUpdated={onAccountEmailUpdated}
      />

      <Card className="border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden">
        <CardHeader className="border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/30">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-teal-600" />
            <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
              Dados pessoais e clínicos
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Telefone de Contato
              </Label>
              <Input
                placeholder="(00) 00000-0000"
                value={values.phone}
                onChange={(e) => updateField('phone', formatPhone(e.target.value))}
                className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">CPF</Label>
              <Input
                placeholder="000.000.000-00"
                value={values.cpf}
                onChange={(e) => updateField('cpf', formatCpf(e.target.value))}
                className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Data de nascimento
              </Label>
              <Input
                type="date"
                max={todayISO}
                value={values.dateOfBirth}
                onChange={(e) => updateField('dateOfBirth', e.target.value)}
                className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Sexo</Label>
              <select
                value={values.gender}
                onChange={(e) => updateField('gender', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Selecione</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="Outro">Prefiro não informar</option>
              </select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                É brasileiro?
              </Label>
              <div className="flex gap-4 items-center h-10">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="isBrazilian_editor"
                    checked={values.isBrazilian === 'true'}
                    onChange={() => {
                      updateField('isBrazilian', 'true');
                      updateField('country', 'Brasil');
                    }}
                    className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 bg-white dark:bg-neutral-900"
                  />
                  Sim
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="isBrazilian_editor"
                    checked={values.isBrazilian === 'false'}
                    onChange={() => {
                      updateField('isBrazilian', 'false');
                      updateField('country', '');
                    }}
                    className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 bg-white dark:bg-neutral-900"
                  />
                  Não
                </label>
              </div>
            </div>
            {values.isBrazilian === 'false' && (
              <div className="space-y-2 md:col-span-2">
                <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  País de origem
                </Label>
                <Input
                  placeholder="Ex: Argentina"
                  value={values.country}
                  onChange={(e) => updateField('country', e.target.value)}
                  className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
                />
              </div>
            )}
            <div className="space-y-2 md:col-span-2">
              <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Contato de emergência
              </Label>
              <Input
                placeholder="Nome e telefone"
                value={values.emergencyContact}
                onChange={(e) => updateField('emergencyContact', e.target.value)}
                className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4 bg-gray-50 dark:bg-neutral-800/30 rounded-2xl border border-gray-100 dark:border-neutral-800/80">
            <div className="space-y-2">
              <Label htmlFor="patient-blood-type" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Tipo Sanguíneo
              </Label>
              <select
                id="patient-blood-type"
                value={values.bloodType}
                onChange={(e) => updateField('bloodType', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Selecione</option>
                {BLOOD_TYPE_SELECT_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Altura (m)
              </Label>
              <Input
                placeholder="Ex: 1.75"
                type="number"
                step="0.01"
                value={values.height}
                onChange={(e) => updateField('height', e.target.value)}
                className={cn(
                  'bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500',
                  fieldErrors.height && 'border-red-500',
                )}
              />
              {fieldErrors.height && (
                <p className="text-xs text-red-600">{fieldErrors.height}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Peso (kg)
              </Label>
              <Input
                placeholder="Ex: 70"
                type="number"
                step="0.1"
                value={values.weight}
                onChange={(e) => updateField('weight', e.target.value)}
                className={cn(
                  'bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500',
                  fieldErrors.weight && 'border-red-500',
                )}
              />
              {fieldErrors.weight && (
                <p className="text-xs text-red-600">{fieldErrors.weight}</p>
              )}
            </div>

            {imc !== null && imcDetails && (
              <div
                className={cn(
                  'md:col-span-3 flex items-center justify-between p-4 rounded-xl border transition-all duration-300',
                  imcDetails.color,
                )}
              >
                <div className="flex items-center gap-3">
                  <Scale className="w-6 h-6" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider opacity-70">
                      IMC (calculado)
                    </p>
                    <p className="text-lg font-black">
                      {imc} — <span className="font-bold">{imcDetails.label}</span>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <ConditionalTextarea
              label="Alergias"
              placeholder="Descreva alergias conhecidas..."
              value={values.allergies}
              onChange={(val) => updateField('allergies', val)}
            />
            <ConditionalTextarea
              label="Medicamentos de uso contínuo"
              placeholder="Liste remédios e dosagens..."
              value={values.medications}
              onChange={(val) => updateField('medications', val)}
            />
            <ConditionalTextarea
              label="Doenças pré-existentes / histórico médico"
              placeholder="Diagnósticos crônicos, cirurgias relevantes..."
              value={values.medicalHistory}
              onChange={(val) => updateField('medicalHistory', val)}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden">
        <CardHeader className="border-b border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/30">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-teal-600" />
            <CardTitle className="text-lg font-bold text-gray-900 dark:text-white">
              Endereço
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">CEP</Label>
            <Input
              placeholder="00000-000"
              value={values.zipCode}
              onChange={(e) => updateField('zipCode', formatCep(e.target.value))}
              className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500 max-w-xs"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Rua</Label>
            <Input
              value={values.street}
              onChange={(e) => updateField('street', e.target.value)}
              className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Número</Label>
            <Input
              value={values.number}
              onChange={(e) => updateField('number', e.target.value)}
              className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Complemento
            </Label>
            <Input
              value={values.complement}
              onChange={(e) => updateField('complement', e.target.value)}
              className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Bairro</Label>
            <Input
              value={values.neighborhood}
              onChange={(e) => updateField('neighborhood', e.target.value)}
              className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Cidade</Label>
            <Input
              value={values.city}
              onChange={(e) => updateField('city', e.target.value)}
              className="bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-gray-700 dark:text-gray-300">UF</Label>
            <Input
              maxLength={2}
              placeholder="SP"
              value={values.state}
              onChange={(e) => updateField('state', e.target.value.toUpperCase())}
              className={cn(
                'bg-white dark:bg-neutral-900 border-gray-200 dark:border-neutral-700 focus:ring-2 focus:ring-teal-500 max-w-[6rem]',
                fieldErrors.state && 'border-red-500',
              )}
            />
            {fieldErrors.state && <p className="text-xs text-red-600">{fieldErrors.state}</p>}
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-teal-600 hover:bg-teal-700 text-white font-bold px-8 gap-2 shadow-sm rounded-xl h-11"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Salvando...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Salvar Meu Perfil
            </>
          )}
        </Button>
      </div>

      <Card className="border-amber-200 dark:border-amber-900/30 shadow-xs bg-amber-50/20 dark:bg-amber-950/5">
        <CardContent className="p-4 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
            <p className="font-bold">Privacidade e prontuário</p>
            <p className="mt-1">
              Estes dados alimentam seu Prontuário Eletrônico e ficam visíveis aos profissionais
              vinculados nas consultas. Nome e email da conta não são alterados aqui.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
